import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from '../utils/toast';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const CustomPageSubmissionDetail = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const { checkPermission } = useAuth();
  
  const [submission, setSubmission] = useState(null);
  const [customPage, setCustomPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  const canUpdate = checkPermission('Update Custom Page Submission');
  const canDelete = checkPermission('Delete Custom Page Submission');

  useEffect(() => {
    fetchSubmissionDetails();
  }, [submissionId]);

  const fetchSubmissionDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/custom-pages/submissions/${submissionId}`);
      
      if (response.data.success) {
        setSubmission(response.data.data);
        // Use the populated custom page data directly
        if (response.data.data.customPage) {
          setCustomPage(response.data.data.customPage);
        }
        
        // Debug log for Checklist
        console.log('Checklist in Detail View:', response.data.data.documentVerifications);
      }
    } catch (error) {
      console.error('Error fetching submission details:', error);
      toast.error('Failed to fetch submission details');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const response = await api.put(`/custom-pages/submissions/${submissionId}`, {
        status: newStatus
      });
      
      if (response.data.success) {
        toast.success('Status updated successfully');
        setSubmission(response.data.data);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this submission?')) {
      return;
    }

    try {
      const response = await api.delete(`/custom-pages/submissions/${submissionId}`);
      
      if (response.data.success) {
        toast.success('Submission deleted successfully');
        navigate('/forms-and-tasks');
      }
    } catch (error) {
      console.error('Error deleting submission:', error);
      toast.error('Failed to delete submission');
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmittingComment(true);
    try {
      const response = await api.post(`/custom-pages/submissions/${submissionId}/comments`, {
        text: newComment.trim()
      });
      
      if (response.data.success) {
        setNewComment('');
        // Refresh submission data
        await fetchSubmissionDetails();
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderFieldValue = (field, value) => {
    if (!value) return '-';

    switch (field.type) {
      case 'upload':
        if (Array.isArray(value) && value.length > 0) {
          return (
            <div className="space-y-1">
              {value.map((file, index) => (
                <div key={index} className="text-sm text-blue-600 hover:text-blue-800">
                  <a href={file.url} target="_blank" rel="noopener noreferrer">
                    {file.name}
                  </a>
                </div>
              ))}
            </div>
          );
        }
        return 'No files uploaded';

      case 'masterdata':
        if (value && typeof value === 'object') {
          return value.clientName || 'N/A';
        }
        return value || '-';

      case 'user':
        if (value && typeof value === 'object') {
          return `${value.firstName} ${value.lastName}`;
        }
        return value || '-';

      case 'date':
        return formatDate(value);

      case 'multitext':
        return (
          <div className="whitespace-pre-wrap text-sm">
            {value}
          </div>
        );

      default:
        return value;
    }
  };

  // Helper function to get field value from submission data
  const getFieldValue = (fieldName) => {
    if (!submission || !submission.data) return null;
    
    // Handle both Map and object data structures
    if (submission.data instanceof Map) {
      return submission.data.get(fieldName);
    } else {
      return submission.data[fieldName];
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">Submission not found</h2>
          <p className="text-gray-600 mt-2">The submission you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/forms-and-tasks')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Back to Forms & Tasks
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/forms-and-tasks')}
          className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Forms & Tasks
        </button>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Submission Details</h1>
            <p className="text-gray-600 mt-2">
              {customPage?.name} - {submission.name}
            </p>
          </div>
          <div className="flex space-x-4">
            {canUpdate && (
              <>
                <button
                  onClick={() => navigate(`/admin/settings/custom-page-submissions/${customPage?._id}/edit/${submissionId}`)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Edit
                </button>
                <select
                  value={submission.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="draft">Draft</option>
                  <option value="submitted">Submitted</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="completed">Completed</option>
                </select>
              </>
            )}
            {canDelete && (
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="space-y-8">
          {/* Submission Info */}
          <div>
            <h2 className="text-xl font-semibold mb-4">Submission Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                  submission.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                  submission.status === 'submitted' ? 'bg-blue-100 text-blue-800' :
                  submission.status === 'approved' ? 'bg-green-100 text-green-800' :
                  submission.status === 'rejected' ? 'bg-red-100 text-red-800' :
                  'bg-purple-100 text-purple-800'
                }`}>
                  {submission.status}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Submitted By</label>
                <p className="text-sm text-gray-900">
                  {submission.submittedBy?.firstName} {submission.submittedBy?.lastName}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Submitted Date</label>
                <p className="text-sm text-gray-900">{formatDate(submission.createdAt)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Deadline</label>
                <p className="text-sm text-gray-900">
                  {submission.deadline ? formatDate(submission.deadline) : '-'}
                </p>
              </div>
            </div>
          </div>

          {/* Form Data */}
          {customPage && (
            <div>
              <h2 className="text-xl font-semibold mb-4">Form Data</h2>
              <div className="space-y-6">
                {customPage.fields.map((field, index) => (
                  <div key={index} className="border-b border-gray-200 pb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {field.label}
                    </label>
                    <div className="text-sm text-gray-900">
                      {renderFieldValue(field, getFieldValue(field.name))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Checklist */}
          <div>
            <h2 className="text-xl font-semibold mb-4">Checklist</h2>
            {submission.documentVerifications && submission.documentVerifications.length > 0 ? (
              <div className="space-y-4">
                {submission.documentVerifications.map((docVer, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-gray-900">{docVer.documentName}</h4>
                        {docVer.documentId?.description && (
                          <p className="text-sm text-gray-600 mt-1">{docVer.documentId.description}</p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Required
                        </span>
                        {docVer.fileUrl && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Available
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {docVer.fileUrl ? (
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-900">{docVer.fileName}</span>
                        </div>
                        <a
                          href={docVer.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          Download
                        </a>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No file available</p>
                    )}
                    
                    {docVer.documentId?.category && (
                      <p className="text-xs text-gray-500 mt-2">
                        Category: {docVer.documentId.category}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 border border-gray-200 rounded-lg">
                <p className="text-gray-500">No Checklist required for this submission.</p>
              </div>
            )}
          </div>

          {/* Comments */}
          <div>
            <h2 className="text-xl font-semibold mb-4">Comments</h2>
            <div className="space-y-4">
              {submission.comments?.map((comment, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-sm font-medium text-gray-900">
                      {comment.createdBy?.firstName} {comment.createdBy?.lastName}
                    </span>
                    <span className="text-sm text-gray-500">
                      {formatDate(comment.createdAt)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700">{comment.text}</p>
                </div>
              ))}
              
              {/* Add Comment Form */}
              <form onSubmit={handleAddComment} className="space-y-2">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={submittingComment || !newComment.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {submittingComment ? 'Adding...' : 'Add Comment'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomPageSubmissionDetail; 